import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { authApi } from '@/api/endpoints';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';

type Step = 'loading' | 'invalid' | 'set-password' | 'setup-totp' | 'success';

interface InviteInfo {
  email: string;
  displayName: string;
  role: string;
  expiresAtUtc: string;
  createdByEmail: string;
}

interface TotpSetup {
  secretBase32: string;
  otpAuthUri: string;
  qrCodePngBase64: string;
  backupCodes: string[];
}

export default function InviteAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const setTokens = useAuthStore(s => s.setTokens);
  const setUser = useAuthStore(s => s.setUser);

  const [step, setStep] = useState<Step>('loading');
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [errorReason, setErrorReason] = useState<string | null>(null);

  // step set-password
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  // step setup-totp
  const [totp, setTotp] = useState<TotpSetup | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setStep('invalid'); setErrorReason('Token ausente na URL.'); return; }
    authApi.getInvite(token)
      .then(info => { setInvite(info); setStep('set-password'); })
      .catch(e => {
        const reason = e?.response?.data?.reason;
        setErrorReason(
          reason === 'invalid_or_expired_token'
            ? 'Este link de convite é inválido, já foi usado ou expirou. Solicite um novo convite a um administrador.'
            : reason ?? e.message ?? 'Erro ao carregar o convite.'
        );
        setStep('invalid');
      });
  }, [token]);

  function proceedToTotp(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Senha muito curta', 'Use uma senha com pelo menos 8 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem', 'Digite a mesma senha nos dois campos.');
      return;
    }
    setSubmitting(true);
    authApi.startInviteTotp(token)
      .then(t => { setTotp(t); setStep('setup-totp'); })
      .catch(e => {
        const reason = e?.response?.data?.reason;
        toast.error('Falha ao iniciar 2FA', reason ?? e.message ?? 'Erro inesperado.');
      })
      .finally(() => setSubmitting(false));
  }

  function finishActivation(e: React.FormEvent) {
    e.preventDefault();
    if (totpCode.length !== 6) {
      toast.error('Código 2FA inválido', 'O código deve ter 6 dígitos.');
      return;
    }
    setSubmitting(true);
    authApi.acceptInvite({ token, newPassword: password, totpCode })
      .then(tokens => {
        setTokens({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          accessExpiresAt: tokens.accessExpiresAtUtc,
          refreshExpiresAt: tokens.refreshExpiresAtUtc
        });
        setUser({
          userId: tokens.userId,
          email: tokens.email,
          displayName: tokens.displayName,
          role: tokens.role as 'admin' | 'manager',
          isActive: true,
          isTotpEnabled: true
        });
        setStep('success');
        toast.success('Conta ativada com sucesso', 'Você já está autenticado. Redirecionando para o Portal…');
        setTimeout(() => navigate('/', { replace: true }), 1800);
      })
      .catch(e => {
        const reason = e?.response?.data?.reason;
        const friendly = reason === 'invalid_code' ? 'O código 2FA está incorreto. Tente novamente.'
          : reason === 'invalid_or_expired_token' ? 'O convite expirou ou já foi usado.'
          : reason === 'password_too_short' ? 'A senha deve ter pelo menos 8 caracteres.'
          : reason ?? e.message ?? 'Erro inesperado.';
        toast.error('Falha ao ativar a conta', friendly);
      })
      .finally(() => setSubmitting(false));
  }

  // ===================== RENDER =====================
  if (step === 'loading') {
    return (
      <CenterWrapper>
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Validando convite…
        </div>
      </CenterWrapper>
    );
  }

  if (step === 'invalid') {
    return (
      <CenterWrapper>
        <Card className="max-w-md w-full">
          <CardHeader className="flex flex-row items-center gap-3 space-y-0">
            <div className="rounded-full bg-destructive/10 p-2"><AlertCircle className="h-5 w-5 text-destructive" /></div>
            <CardTitle>Convite inválido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{errorReason}</p>
            <Link to="/login"><Button variant="outline">Ir para o login</Button></Link>
          </CardContent>
        </Card>
      </CenterWrapper>
    );
  }

  if (step === 'success') {
    return (
      <CenterWrapper>
        <Card className="max-w-md w-full">
          <CardHeader className="flex flex-row items-center gap-3 space-y-0">
            <div className="rounded-full bg-success/10 p-2"><CheckCircle2 className="h-5 w-5 text-success" /></div>
            <CardTitle>Conta ativada!</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Bem-vindo(a) ao CodeShield Portal. Redirecionando…</p>
          </CardContent>
        </Card>
      </CenterWrapper>
    );
  }

  return (
    <CenterWrapper>
      <Card className="max-w-lg w-full">
        <CardHeader className="flex flex-row items-center gap-3 space-y-0">
          <div className="rounded-full bg-primary/10 p-2"><ShieldCheck className="h-5 w-5 text-primary" /></div>
          <div>
            <CardTitle>Ative sua conta</CardTitle>
            {invite && (
              <div className="text-xs text-muted-foreground mt-1">
                {invite.email} · <Badge variant="outline" className="ml-1">{invite.role === 'admin' ? 'Administrador' : 'Analista'}</Badge>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* progresso */}
          <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
            <StepDot active={step === 'set-password'} done={step === 'setup-totp'}>1</StepDot>
            <span className={step === 'set-password' ? 'text-foreground font-medium' : ''}>Definir senha</span>
            <span className="opacity-30">·</span>
            <StepDot active={step === 'setup-totp'}>2</StepDot>
            <span className={step === 'setup-totp' ? 'text-foreground font-medium' : ''}>Configurar 2FA</span>
          </div>

          {step === 'set-password' && (
            <form onSubmit={proceedToTotp} className="space-y-3">
              {invite && (
                <div className="text-sm text-muted-foreground bg-muted/40 border-l-2 border-info p-2 rounded">
                  Olá, <strong>{invite.displayName}</strong>! {invite.createdByEmail ? <>{invite.createdByEmail} </> : 'Um administrador '}criou uma conta para você. Defina sua senha abaixo.
                </div>
              )}
              <div>
                <Label>Nova senha <span className="text-destructive">*</span></Label>
                <div className="flex gap-1">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    className="font-mono"
                    placeholder="Pelo menos 8 caracteres"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowPwd(s => !s)}>
                    {showPwd ? 'Ocultar' : 'Mostrar'}
                  </Button>
                </div>
              </div>
              <div>
                <Label>Confirme a senha <span className="text-destructive">*</span></Label>
                <Input
                  type={showPwd ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="font-mono"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Continuar para 2FA
              </Button>
            </form>
          )}

          {step === 'setup-totp' && totp && (
            <form onSubmit={finishActivation} className="space-y-3">
              <div className="text-sm text-muted-foreground bg-muted/40 border-l-2 border-warning p-2 rounded">
                Escaneie o QR Code abaixo com seu aplicativo autenticador (Google Authenticator, Authy, 1Password etc.) e digite o código de 6 dígitos para finalizar a ativação.
              </div>
              <div className="flex items-start gap-4">
                <img
                  src={`data:image/png;base64,${totp.qrCodePngBase64}`}
                  alt="QR Code 2FA"
                  className="border rounded p-2 bg-white"
                  width={180}
                  height={180}
                />
                <div className="flex-1 space-y-2 text-xs">
                  <div>
                    <Label className="text-[10px] uppercase">Chave manual</Label>
                    <Input value={totp.secretBase32} readOnly className="font-mono text-[11px]" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Códigos de recuperação</Label>
                    <pre className="bg-muted p-2 rounded text-[10px] font-mono whitespace-pre-wrap break-all">
                      {totp.backupCodes.join('\n')}
                    </pre>
                    <div className="text-[10px] text-muted-foreground mt-1">Guarde estes códigos em local seguro. Cada um funciona uma única vez se você perder o acesso ao app autenticador.</div>
                  </div>
                </div>
              </div>
              <div>
                <Label>Código atual do aplicativo <span className="text-destructive">*</span></Label>
                <Input
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  required
                  autoFocus
                  className="font-mono text-lg tracking-widest text-center"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={() => setStep('set-password')} disabled={submitting}>Voltar</Button>
                <Button type="submit" disabled={submitting || totpCode.length !== 6} className="flex-1">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Ativar conta e entrar
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </CenterWrapper>
  );
}

function CenterWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      {children}
    </div>
  );
}

function StepDot({ active, done, children }: { active?: boolean; done?: boolean; children: React.ReactNode }) {
  const cls = done
    ? 'bg-success text-success-foreground'
    : active
      ? 'bg-primary text-primary-foreground'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center justify-center rounded-full w-5 h-5 text-[10px] font-bold ${cls}`}>
      {done ? '✓' : children}
    </span>
  );
}
